// @flow
import React, { Fragment } from 'react'
import classNames from 'classnames/bind'

import {
  Table,
  Tr,
  Th,
  Td,
  useCopyColumnsWidths,
  useHover,
  useCopyContentWidths,
  useHoverByClass,
} from 'app/common/ui-components/table'

import CSSSticky from 'app/common/ui-components/css-sticky'
import useSortState from 'app/common/hooks/useSortState'
import HScrollbarsWShadow, { useSyncScrolls } from 'app/common/ui-components/h-scrollbars-w-shadow'
import DetailsHeader from 'app/components/common/reports/DetailsHeader'
import { isValueZero } from 'app/common/templates-next/table-template'

import CellContentOverflow from '../../../common/CellContent/CellContentOverflow'
import useActualSchools from './useActualSchools'
import type { School, Daily, Monthly, DailyTotals, MonthlyTotals } from '../types'

import styles from './Schools.scss'

/**
 *  Отчет с детализацией по всем или нескольким организациям
 */

const sameColumnBase = 'column_'
const cx = classNames.bind(styles)

type Props = {
  schools: Array<School<Daily>> | Array<School<Monthly>>,
  totals: Array<DailyTotals> | Array<MonthlyTotals>,
}

const Schools = ({ schools, totals }: Props) => {
  const [{ sortDirection, sortKey }, handleSort] = useSortState('up', 'schoolTitle')
  const [headers, schoolsActual, totalsActual] = useActualSchools(
    schools,
    totals,
    sortKey,
    sortDirection,
  )

  const [refFrom, refTo] = useCopyColumnsWidths(/* updateKey */)
  const [topScrollerProps, middleScrollerProps, bottomScrollerProps, updateBottomScroll] =
    useSyncScrolls()
  const [elementSource, elementsTarget] = useCopyContentWidths(1, updateBottomScroll)

  const [refTableBody, onMouseEnter, onMouseLeave] = useHover({ numberOfChunks: 3 })
  const [onCellMouseEnter, onCellMouseLeave] = useHoverByClass(sameColumnBase)

  return (
    <Fragment>
      <DetailsHeader reportTitle="Продажи" />
      <CSSSticky
        number={3}
        className={styles.stickableTableHeader}
        stickyClassName={styles.tableSticked}
        shadowClassName={styles.shadow}
      >
        {/* ПЕРВЫЙ СЛОЙ -- ЗАГОЛОВОК */}
        <Table className={styles.table__fixed1}>
          <thead>
            <Tr header>
              <Th
                scope="col"
                id="header_school_title"
                sortKey="schoolTitle"
                sortDirection={sortKey === 'schoolTitle' ? sortDirection : undefined}
                onSortArrowClick={handleSort}
                alignLeft
              >
                {'Школа'}
              </Th>
            </Tr>
          </thead>
        </Table>

        <div className={styles.hScrollableContent}>
          <HScrollbarsWShadow
            hideVerticalScrollBar
            hideHorizontalScrollBar
            absolutelyHideHorizontal_FixFF
            {...topScrollerProps}
          >
            <Table reference={elementsTarget}>
              <thead>
                <Tr header reference={refTo}>
                  {headers.map((headerTitle, i) => (
                    <Th
                      scope="col"
                      id={`header_date_${i}`}
                      key={headerTitle}
                      className={`${styles.headerDate} ${sameColumnBase}${i}`}
                      onMouseEnter={onCellMouseEnter}
                      onMouseLeave={onCellMouseLeave}
                      alignRight
                    >
                      {headerTitle}
                    </Th>
                  ))}
                </Tr>
              </thead>
            </Table>
          </HScrollbarsWShadow>
        </div>

        <Table className={styles.table__fixed2}>
          <thead>
            <Tr header>
              <Th
                scope="col"
                id="header_total_by_school"
                sortKey="total"
                sortDirection={sortKey === 'total' ? sortDirection : undefined}
                onSortArrowClick={handleSort}
                alignRight
              >
                {'Всего'}
              </Th>
            </Tr>
          </thead>
        </Table>
      </CSSSticky>
      {/* ВТОРОЙ СЛОЙ -- КОНТЕНТ */}
      <div className={styles.content}>
        <Table className={styles.table__fixed1}>
          <thead>
            <Tr header hidden>
              <Th
                scope="col"
                // id="header_school_title"
              >
                {'Школа'}
              </Th>
            </Tr>
          </thead>

          <tbody ref={refTableBody}>
            {schoolsActual.map(({ id, schoolTitle }, i) => (
              <Tr
                key={id}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                className={`index_${i}`}
              >
                <Th id={`header_school_${i}`} headers="header_school_title" scope="row" alignLeft>
                  <CellContentOverflow className={styles.cellSchoolName}>
                    {schoolTitle}
                  </CellContentOverflow>
                </Th>
              </Tr>
            ))}
          </tbody>
          <tfoot>
            <Tr hidden>
              <Th scope="row" alignRight>
                {'Итого:'}
              </Th>
            </Tr>
          </tfoot>
        </Table>

        <div className={styles.hScrollableContent}>
          <HScrollbarsWShadow
            hideVerticalScrollBar
            hideHorizontalScrollBar
            absolutelyHideHorizontal_FixFF
            {...middleScrollerProps}
          >
            <Table
              reference={elementSource}
              className={styles.tableMain} // иначе тaблица скролится по вертикали
            >
              <thead>
                <Tr header hidden reference={refFrom}>
                  {headers.map((headerTitle) => (
                    <Th scope="col" key={headerTitle} className={styles.headerDate} alignRight>
                      {headerTitle}
                    </Th>
                  ))}
                </Tr>
              </thead>
              <tbody ref={refTableBody}>
                {schoolsActual.map(({ id, data }, i) => (
                  <Tr
                    key={id}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    className={`index_${i}`}
                  >
                    {data.map((value, j) => (
                      <Td
                        key={j}
                        headers={`header_date_${j} header_school_${i}`}
                        className={cx(`${sameColumnBase}${j}`, { zero: isValueZero(value) })}
                        onMouseEnter={onCellMouseEnter}
                        onMouseLeave={onCellMouseLeave}
                        alignRight
                      >
                        {value}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </tbody>
              <tfoot>
                <Tr hidden>
                  {totalsActual.map((value, i) => (
                    <Td key={i}>{value}</Td>
                  ))}
                </Tr>
              </tfoot>
            </Table>
          </HScrollbarsWShadow>
        </div>

        <Table className={styles.table__fixed2}>
          <thead>
            <Tr header hidden>
              <Th scope="col">{'Всего'}</Th>
            </Tr>
          </thead>
          <tbody ref={refTableBody}>
            {schoolsActual.map(({ id, totalString }, i) => (
              <Tr
                key={id}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                className={cx(`index_${i}`, { zero: isValueZero(totalString) })}
              >
                <Td headers="header_total_by_school" alignRight>
                  <CellContentOverflow className={styles.cellTotal}>
                    {totalString}
                  </CellContentOverflow>
                </Td>
              </Tr>
            ))}
          </tbody>
          <tfoot>
            <Tr hidden>
              <Td>&nbsp;</Td>
            </Tr>
          </tfoot>
        </Table>
      </div>
      {/* ТРЕТИЙ СЛОЙ -- ПОДВАЛ */}
      <CSSSticky number={-1} shadowClassName={styles.stickyBottom} className={styles.footer}>
        <div className={styles.content}>
          <Table className={styles.table__fixed1}>
            <tfoot>
              <Tr>
                <Th headers="header_school_title" id="header_total" scope="row" alignRight>
                  {'Итого:'}
                </Th>
              </Tr>
            </tfoot>
          </Table>

          <div className={styles.hScrollableContent}>
            <HScrollbarsWShadow hideVerticalScrollBar {...bottomScrollerProps}>
              <Table reference={elementsTarget}>
                <tfoot>
                  <Tr reference={refTo} className={styles.footerRow}>
                    {totalsActual.map((value, i) => (
                      <Td
                        key={i}
                        headers={`header_date_${i} header_total`}
                        className={cx(`${sameColumnBase}${i}`, { zero: isValueZero(value) })}
                        onMouseEnter={onCellMouseEnter}
                        onMouseLeave={onCellMouseLeave}
                        alignRight
                      >
                        {value}
                      </Td>
                    ))}
                  </Tr>
                </tfoot>
              </Table>
            </HScrollbarsWShadow>
          </div>

          <Table className={styles.table__fixed2}>
            <tfoot>
              <Tr>
                <Td>&nbsp;</Td>
              </Tr>
            </tfoot>
          </Table>
        </div>
      </CSSSticky>
    </Fragment>
  )
}

export default Schools
